import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Box, Button, CircularProgress, Grid, Stack, TextField, Typography } from "@mui/material";
import { LoadingButton as MuiLoadingButton } from "@mui/lab";
import styled from "@emotion/styled";

import { ReactComponent as ContinueIcon } from "../../../vendor/arrow_right.svg";
import useGuest from "../../../hooks/super/useGuest";
import { SnackbarAlert } from "../../../components/pages/common/styled";
import GenerateCertAIOModal from "../../../components/pages/application/ssl/M_GenerateCertAIO";
import UploadCertAIOModal from "../../../components/pages/application/ssl/M_UploadCertAIO";

const LoadingButton = styled(MuiLoadingButton)`
  font-size: 15px;
`;

const DomainStatus = {
  NONE: 0,
  PENDING: 1,
  SELECTED: 2,
  CERT_PENDING: 3,
  CERT_ADDED: 4,
};

function OnboardAIO() {
  const { checkDomainName, setErr, errMsg, finishOnboarding } = useGuest();

  const domainNameRef = React.useRef(null);
  const [domainName, setDomainName] = useState("");
  const [domainStatus, setDomainStatus] = useState(DomainStatus.NONE);

  const [generateCertOpen, setGenerateCertOpen] = useState(false);
  const handleGenerateCertOpen = () => setGenerateCertOpen(true);
  const handleGenerateCertClose = () => setGenerateCertOpen(false);

  // modal open start
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const [snackOpen, setSnackOpen] = useState(false);
  const handleSnackClose = () => {
    setSnackOpen(false);
    setErr(null);
  };
  const [message, setMessage] = useState();
  const [success, setSuccess] = useState();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleReset = () => {
    setDomainStatus(DomainStatus.NONE);
    setMessage(null);
    setSuccess("error");
    setIsSubmitting(false);
    setDomainName("");
  };

  const changeDomainName = (event) => {
    setDomainName(event.target.value);
  };

  const handleCompleteUpload = async () => {
    setDomainStatus(DomainStatus.CERT_ADDED);
  };

  const handleCompleteGenerateCert = async () => {
    setDomainStatus(DomainStatus.CERT_ADDED);
  };

  const handleAction = async () => {
    if (domainStatus === DomainStatus.NONE) {
      if (!domainName) {
        setErr("Please input the domain name");
        return;
      }
      setDomainStatus(DomainStatus.PENDING);
      const res = await checkDomainName(domainName);
      if (res?.status) {
        setDomainStatus(DomainStatus.SELECTED);
      } else {
        setDomainStatus(DomainStatus.NONE);
      }
    } else if (domainStatus === DomainStatus.CERT_ADDED) {
      setIsSubmitting(true);
      const res = await finishOnboarding();
      if (res) {
        setIsSubmitting(false);
        window.location.href = `https://${domainName}`;
      } else {
        setIsSubmitting(false);
      }
    }
  };

  useEffect(() => {
    if (errMsg) {
      setMessage(errMsg);
      setSuccess("error");
      setSnackOpen(true);
    } else {
      setMessage(null);
      setSnackOpen(false);
    }
  }, [errMsg]);

  return (
    <React.Fragment>
      <Helmet title="DNS Setting - AIO Onboarding" />
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
        }}
      >
        <Box
          sx={{
            backgroundColor: "white",
            p: 5,
            borderRadius: 2,
            boxShadow: 3,
            maxWidth: 1440,
            width: "100%",
            minHeight: "480px",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Typography variant="h1" align="center" gutterBottom>
            DNS Setting Onboarding
          </Typography>

          <Grid container spacing={3} mt={5}>
            <Grid item xs={12}>
              <Typography variant="h2" mb={1}>
                Domain Name
              </Typography>
              <TextField
                variant="outlined"
                fullWidth
                required
                disabled={domainStatus !== DomainStatus.NONE}
                sx={{ background: "white", borderRadius: 2 }}
                value={domainName}
                onChange={changeDomainName}
                inputRef={domainNameRef}
              />
            </Grid>
          </Grid>

          <Box 
            mt={5} 
            sx={{ 
              minHeight: "120px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {domainStatus === DomainStatus.PENDING && (
              <CircularProgress color="success" />
            )}
            {domainStatus > DomainStatus.PENDING && (
              <Box sx={{ width: "100%" }}>
                <Typography variant="h2" mb={2}>
                  Install SSL Certificates
                </Typography>
                <Grid container spacing={3} justifyContent="space-between">
                  <Grid item xs={12} sm={6}>
                    <Button
                      fullWidth
                      variant="outlined"
                      color="primary"
                      onClick={handleGenerateCertOpen}
                      sx={{ padding: "12px", borderRadius: "8px" }}
                    >
                      Universal
                    </Button>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Button fullWidth variant="outlined" color="primary" onClick={handleOpen} sx={{ padding: "12px", borderRadius: "8px" }}>
                      Upload SSL Certificate
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            )}
          </Box>

          <Stack alignItems="center" direction="row" justifyContent="center" spacing={3} mt={5}>
            {domainStatus > DomainStatus.PENDING && (
              <LoadingButton
                variant="contained"
                color="warning"
                size="large"
                loadingPosition="center"
                loading={isSubmitting || domainStatus === DomainStatus.CERT_PENDING}
                onClick={handleReset}
              >
                Reset
              </LoadingButton>
            )}
            <LoadingButton
              disableRipple
              variant="contained"
              color="success"
              size="large"
              loadingPosition="center"
              loading={isSubmitting || domainStatus === DomainStatus.PENDING || domainStatus === DomainStatus.CERT_PENDING}
              disabled={domainStatus === DomainStatus.SELECTED}
              onClick={handleAction}
              endIcon={<ContinueIcon />}
            >
              {domainStatus === DomainStatus.CERT_ADDED ? "Finish" : "Continue"}
            </LoadingButton>
          </Stack>
          <UploadCertAIOModal open={open} domain={domainName} handleClose={handleClose} handleComplete={handleCompleteUpload} />
          <GenerateCertAIOModal
            open={generateCertOpen}
            domain={domainName}
            handleClose={handleGenerateCertClose}
            handleComplete={handleCompleteGenerateCert}
          />
          <SnackbarAlert open={snackOpen} onClose={handleSnackClose} severity={success} message={message} />
        </Box>
      </Box>
    </React.Fragment>
  );
}

export default OnboardAIO;
